/**
 * Better-Auth client: configured auth plugins, session atom, and the
 * `useSession` hook used app-wide.
 *
 * Wraps Better-Auth's `createAuthClient` with PSI-specific plugins. The
 * `bearer` plugin moves the session token through `localStorage` and an
 * `Authorization` header for environments without cookies (native webview,
 * cross-origin embed); Better Auth's `oauthPopupClient` plugin signs in
 * through a popup and stores the session token where `bearer` reads it; the
 * `firebase` plugin runs Google sign-in through the Firebase SDK and exchanges
 * its ID token for a session; the `testing` plugin exposes a
 * `/sign-in/testing` endpoint that lets E2E tests mint a session without going
 * through a real IdP. Which plugins load is decided once at module load via
 * `EXPO_PUBLIC_DATABASE_ADAPTER`, and only Firebase silos load `firebase`.
 */

import type { BetterAuthClientPlugin, SessionQueryParams, StrictEndpoint, ZodAny, ZodObject, ZodOptional, ZodString } from 'better-auth';
import { oauthPopupClient, POPUP_TOKEN_STORAGE_KEY } from 'better-auth/client/plugins';
import type { BetterFetchError, PreinitializedWritableAtom } from 'better-auth/react';
import { createAuthClient } from 'better-auth/react';
import { GoogleAuthProvider, inMemoryPersistence, setPersistence, signInWithPopup, signOut } from 'firebase/auth';
import { use } from 'react';
import { getFirebaseAuth } from 'system/firebase-init';
import { fireAndForget } from 'util/hooks';

import { getApiBaseUrl } from './util';

type TokenStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/**
 * The browser's local storage or, where local storage is unavailable,
 * storage that lasts only as long as the page.
 */
function getTokenStorage(): TokenStorage {
    try {
        if (window.localStorage) {
            return window.localStorage;
        }
    } catch {
        // A browser that blocks storage throws on access.
    }

    const memory = new Map<string, string>();
    return {
        getItem: (key) => memory.get(key) ?? null,
        setItem: (key, value) => memory.set(key, value),
        removeItem: (key) => memory.delete(key),
    };
}

/**
 * Holds the session token under the `oauthPopupClient` plugin's key, so a
 * token from a popup sign-in and one from a `set-auth-token` header are the
 * same token.
 */
const tokenStorage = getTokenStorage();

const GET_SESSION_URL = `${getApiBaseUrl()}auth/get-session`;
const SIGN_OUT_URL = `${getApiBaseUrl()}auth/sign-out`;

export const usesFirebaseAuth = process.env.EXPO_PUBLIC_DATABASE_ADAPTER !== 'mongodb';

/**
 * Attach the stored session token as an `Authorization` header, if one exists
 * and the request does not carry one already.
 */
function signRequestWithBearerToken(headers: Headers): void {
    const token = tokenStorage.getItem(POPUP_TOKEN_STORAGE_KEY);
    if (token && !headers.has('authorization')) {
        headers.set('authorization', `Bearer ${token}`);
    }
}

const bearer = () => {
    return {
        id: 'bearer',
        getActions: () => ({
            /**
             * Attach this plugin's authentication header(s) to the given
             * `Headers` object before an outgoing API request. Used by
             * non-`authClient.$fetch` callers (e.g. the oRPC `RPCLink`'s
             * `fetch` hook) so they don't have to know what auth scheme
             * is in play — the plugin owns it.
             */
            signRequest: signRequestWithBearerToken,
        }),
        fetchPlugins: [
            {
                id: 'bearer',
                name: 'bearer',
                hooks: {
                    // Deliberately does not set `ctx.credentials = 'omit'` on
                    // get-session: Better Auth already resolves whichever of
                    // the cookie or the bearer header actually validates,
                    // and a stale one does not shadow a valid other one
                    // (verified against the live server: valid cookie +
                    // invalid bearer, and invalid cookie + valid bearer,
                    // both return the session). Omitting the cookie only
                    // removed one of the two ways this call could succeed —
                    // e.g. right after an OAuth callback, when a valid
                    // cookie exists but no bearer token has been stored yet
                    // (the token only comes from a `$fetch` response header,
                    // which a top-level IdP redirect never produces), or
                    // when a stored bearer token has gone stale while the
                    // cookie is still fresh. Both cases left the client
                    // unable to recognize a session the server already has.
                    onRequest: (ctx) => {
                        signRequestWithBearerToken(ctx.headers);
                    },
                    onResponse: async (ctx) => {
                        const bearerToken = ctx.response.headers.get('set-auth-token');
                        if (bearerToken) {
                            tokenStorage.setItem(POPUP_TOKEN_STORAGE_KEY, bearerToken);
                        }

                        const status = ctx.response.status;
                        const url = String(ctx.request.url);
                        if (status === 401) {
                            tokenStorage.removeItem(POPUP_TOKEN_STORAGE_KEY);
                        } else if (url === GET_SESSION_URL) {
                            const response = ctx.response.clone();
                            const data = await response.json();
                            if (data === null) {
                                tokenStorage.removeItem(POPUP_TOKEN_STORAGE_KEY);
                            }
                        } else if (url === SIGN_OUT_URL) {
                            tokenStorage.removeItem(POPUP_TOKEN_STORAGE_KEY);
                        }
                    },
                },
            },
        ],
    } satisfies BetterAuthClientPlugin;
};

const firebase = () => {
    return {
        id: 'firebase',
        getActions: ($fetch) => ({
            signIn: {
                /**
                 * Signs in with Google through the Firebase SDK, takes its ID
                 * token, signs out of the SDK, and exchanges the token for a
                 * session, letting Better Auth take over managing the session.
                 *
                 * TODO: Remove once Google sign-in goes through Better Auth's
                 * Google provider.
                 */
                google: async () => {
                    const auth = getFirebaseAuth();
                    const provider = new GoogleAuthProvider();
                    const credential = await signInWithPopup(auth, provider);
                    const idToken = await credential.user.getIdToken();
                    await signOut(auth);
                    return $fetch('/sign-in/firebase', { method: 'POST', body: { idToken } });
                },
            },
        }),
    } satisfies BetterAuthClientPlugin;
};

const testing = () => {
    return {
        id: 'testing',
        // TODO: Reference the server plugin once server and client can share
        // code.
        $InferServerPlugin: {} as {
            id: 'testing';
            endpoints: {
                signInTesting: StrictEndpoint<
                    '/sign-in/testing',
                    {
                        method: 'POST';
                        body: ZodObject<{
                            id: ZodString;
                            data: ZodOptional<ZodAny>;
                        }>;
                    },
                    {}
                >;
            };
        },
    } satisfies BetterAuthClientPlugin;
};

const authPlugins = usesFirebaseAuth ? [bearer(), oauthPopupClient(), firebase(), testing()] : [bearer(), oauthPopupClient(), testing()];

/**
 * Keeps Firebase sign-ins only in memory, and discards one that an earlier
 * client stored in IndexedDB. Neither call needs awaiting. Firebase Auth
 * queues both within moments, long before a sign-in through a popup can queue
 * its own update.
 *
 * TODO: Stop signing out once no client sends Firebase ID tokens in place of
 * sessions.
 */
function discardStoredFirebaseSignIn(): void {
    const auth = getFirebaseAuth();
    fireAndForget(setPersistence(auth, inMemoryPersistence));
    fireAndForget(signOut(auth));
}

if (usesFirebaseAuth && !process.env.STORYBOOK) {
    discardStoredFirebaseSignIn();
}

export const authClient = createAuthClient({
    baseURL: `${getApiBaseUrl()}auth`,
    plugins: process.env.STORYBOOK ? [] : authPlugins,
});

type Session = typeof authClient.$Infer.Session.session;
export type User = typeof authClient.$Infer.Session.user;

/**
 * The Better Auth (internal) session atom.
 * @deprecated in favor of the `useSession` hook.
 */
const sessionAtom = authClient.$store.atoms.session as PreinitializedWritableAtom<{
    data: null | { user: User; session: Session };
    error: null | BetterFetchError;
    isPending: boolean;
    isRefetching: boolean;
    refetch: (queryParams?: { query?: SessionQueryParams } | undefined) => Promise<void>;
}>;

let initialFetchHasSettled = false;
let initialFetchSettled: Promise<void> | undefined;

/**
 * Whether the session's first fetch has settled, whatever its result. Better
 * Auth sets `isPending` again on every fetch while signed out, so only the
 * first time it clears tells a cold load from a signed-out reader.
 */
function hasInitialFetchSettled(): boolean {
    if (!initialFetchHasSettled && !sessionAtom.get().isPending) {
        initialFetchHasSettled = true;
    }
    return initialFetchHasSettled;
}

/**
 * Resolves once the session's first fetch settles. It's created once, because
 * React's `use` tracks a promise by identity. It starts listening on first use
 * rather than at module load, since listening mounts the atom and starts a
 * session fetch.
 */
function waitForInitialFetch(): Promise<void> {
    initialFetchSettled ??= new Promise((resolve) => {
        const unlisten = sessionAtom.listen((session) => {
            if (session.isPending) {
                return;
            }

            initialFetchHasSettled = true;
            unlisten();
            resolve();
        });
    });
    return initialFetchSettled;
}

/**
 * `useSession`, suspending until the session's first fetch settles, so the
 * caller never sees a session that hasn't loaded yet.
 *
 * A caller can't tell a failed first fetch from a signed-out reader. The
 * failed fetch counts as settled, and the session reads as signed out until a
 * later fetch succeeds. Better Auth fetches again when the page becomes
 * visible or the browser comes back online, and on any sign-in or sign-out.
 *
 * In a server render the hook suspends forever, since Better Auth fetches the
 * session only in the browser. To render on the server, the hook would have
 * to throw there before it touches the session atom, which makes React send
 * the fallback and render the boundary again in the browser.
 */
// TODO: Use Better Auth's useSession once it suspends
// (better-auth/better-auth#11219).
export function useSuspenseSession() {
    const session = useSession();

    if (!hasInitialFetchSettled()) {
        use(waitForInitialFetch());
    }

    return session;
}

/**
 * Register a callback to be notified when the auth state changes.
 * @deprecated in favor of the `useSession` hook.
 * @param callback - Function to call when auth state changes
 * @returns Cleanup function to unregister the callback
 */
export function onUserChanged(callback: (user: User | null) => void): () => void {
    return sessionAtom.subscribe((session) => {
        if (!session.isPending && !session.isRefetching) {
            callback(session.data?.user ?? null);
        }
    });
}

export const { useSession } = authClient;
